import * as React from 'react';
import { motion } from 'framer-motion';
import { Package, Plus, Search, Edit2, Trash2, Upload, AlertTriangle } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { DataTable, type Column } from '@/components/ui/data-table';
import { Modal } from '@/components/ui/modal';
import { ProductImportModal } from '@/components/ProductImportModal';
import { RequireRole } from '@/lib/auth';
import { apiFetch } from '@/lib/api';

interface Product {
  id: string;
  barcode: string;
  internal_barcode: string | null;
  name: string;
  sale_price: number;
  cost_price: number;
  unit: string;
  category: string;
  stock_quantity: number;
  min_stock_level: number;
  supplier_id: string | null;
  status: 'normal' | 'baixo' | 'esgotado';
  created_at: string;
  updated_at: string;
}

interface Supplier {
  id: string;
  company_name: string;
}

interface ProductFormData {
  barcode: string;
  name: string;
  sale_price: string;
  cost_price: string;
  unit: string;
  category: string;
  stock_quantity: string;
  min_stock_level: string;
  supplier_id: string;
}

interface Pagination {
  total: number;
  page: number;
  limit: number;
  totalPages: number;
}

const STATUS_VARIANTS: Record<string, 'success' | 'warning' | 'destructive'> = {
  normal: 'success',
  baixo: 'warning',
  esgotado: 'destructive',
};

const STATUS_LABELS: Record<string, string> = {
  normal: 'Normal',
  baixo: 'Baixo',
  esgotado: 'Esgotado',
};

function formatBRL(value: number): string {
  return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(value);
}

function ProductsPageContent() {
  const [products, setProducts] = React.useState<Product[]>([]);
  const [suppliers, setSuppliers] = React.useState<Supplier[]>([]);
  const [loading, setLoading] = React.useState(true);
  const [search, setSearch] = React.useState('');
  const [debouncedSearch, setDebouncedSearch] = React.useState('');
  const [categoryFilter, setCategoryFilter] = React.useState('');
  const [statusFilter, setStatusFilter] = React.useState('');
  const [pagination, setPagination] = React.useState<Pagination>({ total: 0, page: 1, limit: 20, totalPages: 0 });
  const [modalOpen, setModalOpen] = React.useState(false);
  const [importModalOpen, setImportModalOpen] = React.useState(false);
  const [editingProduct, setEditingProduct] = React.useState<Product | null>(null);
  const [deleteConfirm, setDeleteConfirm] = React.useState<Product | null>(null);
  const [formData, setFormData] = React.useState<ProductFormData>({
    barcode: '',
    name: '',
    sale_price: '',
    cost_price: '',
    unit: 'un',
    category: '',
    stock_quantity: '0',
    min_stock_level: '0',
    supplier_id: '',
  });
  const [formErrors, setFormErrors] = React.useState<Partial<Record<keyof ProductFormData, string>>>({});
  const [stockEntryMode, setStockEntryMode] = React.useState<{ productName: string; currentStock: number } | null>(null);
  const [submitting, setSubmitting] = React.useState(false);
  const [categories, setCategories] = React.useState<string[]>([]);

  // Check barcode when user finishes typing (blur or enter)
  const checkBarcode = React.useCallback(async (barcode: string) => {
    if (!barcode.trim() || editingProduct) return;
    try {
      const response = await apiFetch(`/api/products/barcode/${encodeURIComponent(barcode)}`);
      if (response.ok) {
        const product = await response.json();
        // Product exists! Switch to stock entry mode
        setStockEntryMode({ productName: product.name, currentStock: Number(product.stock_quantity) });
        setFormData((p) => ({
          ...p,
          name: product.name,
          sale_price: String(product.sale_price),
          cost_price: String(product.cost_price || ''),
          unit: product.unit || 'un',
          category: product.category || 'Geral',
          stock_quantity: '',
          min_stock_level: String(product.min_stock_level || '0'),
        }));
      } else {
        // Product doesn't exist - normal creation mode
        setStockEntryMode(null);
      }
    } catch {
      setStockEntryMode(null);
    }
  }, [editingProduct]);

  // Debounce search input
  React.useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(search), 300);
    return () => clearTimeout(timer);
  }, [search]);

  const fetchProducts = React.useCallback(async (page = 1) => {
    try {
      const params = new URLSearchParams();
      params.set('page', String(page));
      params.set('limit', '20');
      if (debouncedSearch) params.set('search', debouncedSearch);
      if (categoryFilter) params.set('category', categoryFilter);
      if (statusFilter) params.set('status', statusFilter);

      const response = await apiFetch(`/api/products?${params.toString()}`);
      if (response.ok) {
        const data = await response.json();
        setProducts(data.data);
        setPagination(data.pagination);
      }
    } catch {
      toast.error('Erro ao carregar produtos');
    } finally {
      setLoading(false);
    }
  }, [debouncedSearch, categoryFilter, statusFilter]);

  const fetchCategories = React.useCallback(async () => {
    try {
      const response = await apiFetch('/api/products/categories');
      if (response.ok) {
        const data = await response.json();
        setCategories(data.data || []);
      }
    } catch {
      // Silently fail
    }
  }, []);

  const fetchSuppliers = React.useCallback(async () => {
    try {
      const response = await apiFetch('/api/suppliers?limit=100');
      if (response.ok) {
        const data = await response.json();
        setSuppliers(data.data);
      }
    } catch {
      // Silently fail - suppliers are optional
    }
  }, []);

  React.useEffect(() => {
    fetchProducts();
    fetchSuppliers();
  }, [fetchProducts, fetchSuppliers]);

  React.useEffect(() => {
    fetchCategories();
  }, [fetchCategories]);

  function openCreateModal() {
    setEditingProduct(null);
    setStockEntryMode(null);
    setFormData({
      barcode: '',
      name: '',
      sale_price: '',
      cost_price: '',
      unit: 'un',
      category: '',
      stock_quantity: '0',
      min_stock_level: '0',
      supplier_id: '',
    });
    setFormErrors({});
    setModalOpen(true);
  }

  function openEditModal(product: Product) {
    setEditingProduct(product);
    setFormData({
      barcode: product.barcode,
      name: product.name,
      sale_price: String(product.sale_price),
      cost_price: String(product.cost_price),
      unit: product.unit,
      category: product.category,
      stock_quantity: String(product.stock_quantity),
      min_stock_level: String(product.min_stock_level),
      supplier_id: product.supplier_id || '',
    });
    setFormErrors({});
    setModalOpen(true);
  }

  function validateForm(): boolean {
    const errors: Partial<Record<keyof ProductFormData, string>> = {};
    if (!formData.barcode.trim()) errors.barcode = 'Código de barras é obrigatório';
    if (!formData.name.trim()) errors.name = 'Nome é obrigatório';
    if (!formData.sale_price || Number(formData.sale_price) <= 0) errors.sale_price = 'Preço de venda deve ser maior que 0';
    if (formData.stock_quantity === '') errors.stock_quantity = 'Quantidade é obrigatória';
    
    
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!validateForm()) return;

    setSubmitting(true);
    try {
      const body = {
        barcode: formData.barcode,
        name: formData.name,
        sale_price: Number(formData.sale_price),
        cost_price: formData.cost_price ? Number(formData.cost_price) : 0,
        unit: formData.unit,
        category: formData.category || 'Geral',
        stock_quantity: Number(formData.stock_quantity),
        min_stock_level: Number(formData.min_stock_level),
        supplier_id: formData.supplier_id || null,
      };

      if (editingProduct) {
        const response = await apiFetch(`/api/products/${editingProduct.id}`, {
          method: 'PATCH',
          body: JSON.stringify(body),
        });
        if (!response.ok) {
          const err = await response.json();
          throw new Error(err.error || 'Erro ao atualizar');
        }
        toast.success('Produto atualizado com sucesso');
      } else {
        const response = await apiFetch('/api/products', {
          method: 'POST',
          body: JSON.stringify(body),
        });
        if (!response.ok) {
          const err = await response.json();
          throw new Error(err.error || 'Erro ao criar');
        }
        const result = await response.json();
        if (result._action === 'stock_added') {
          toast.success(result._message || 'Estoque atualizado com sucesso');
        } else if (result._action === 'already_exists') {
          toast.info(result._message || 'Produto já cadastrado');
        } else {
          toast.success('Produto criado com sucesso');
        }
      }

      setModalOpen(false);
      fetchProducts(pagination.page);
      fetchCategories();
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Erro inesperado';
      toast.error(message);
    } finally {
      setSubmitting(false);
    }
  }

  async function handleDelete() {
    if (!deleteConfirm) return;
    try {
      const response = await apiFetch(`/api/products/${deleteConfirm.id}`, { method: 'DELETE' });
      if (!response.ok && response.status !== 204) {
        const err = await response.json();
        throw new Error(err.error || 'Erro ao excluir');
      }
      toast.success('Produto excluído com sucesso');
      setDeleteConfirm(null);
      fetchProducts(pagination.page);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Erro inesperado';
      toast.error(message);
    }
  }

  const columns: Column<Product>[] = [
    {
      key: 'barcode',
      header: 'Código',
      sortable: true,
      render: (row) => <span className="font-mono text-xs">{row.barcode}</span>,
    },
    {
      key: 'name',
      header: 'Nome',
      sortable: true,
      render: (row) => <span className="font-medium">{row.name}</span>,
    },
    {
      key: 'category',
      header: 'Categoria',
      sortable: true,
    },
    {
      key: 'sale_price',
      header: 'Preço Venda',
      render: (row) => <span className="text-green-400">{formatBRL(Number(row.sale_price))}</span>,
    },
    {
      key: 'cost_price',
      header: 'Preço Custo',
      render: (row) => <span className="text-muted-foreground">{formatBRL(Number(row.cost_price))}</span>,
    },
    {
      key: 'margin',
      header: 'Margem',
      render: (row) => {
        const cost = Number(row.cost_price);
        const sale = Number(row.sale_price);
        const margin = cost > 0 ? ((sale - cost) / cost * 100).toFixed(0) : '—';
        return <span className="text-sm text-muted-foreground">{margin}%</span>;
      },
    },
    {
      key: 'stock_quantity',
      header: 'Estoque',
      render: (row) => <span>{Number(row.stock_quantity)}</span>,
    },
    {
      key: 'status',
      header: 'Status',
      render: (row) => (
        <Badge variant={STATUS_VARIANTS[row.status] || 'default'} dot>
          {STATUS_LABELS[row.status] || row.status}
        </Badge>
      ),
    },
    {
      key: 'actions',
      header: '',
      className: 'w-24',
      render: (row) => (
        <div className="flex items-center gap-1">
          <button
            onClick={() => openEditModal(row)}
            className="rounded-md p-1.5 text-muted-foreground hover:bg-accent hover:text-foreground transition-colors"
            aria-label={`Editar ${row.name}`}
          >
            <Edit2 className="h-4 w-4" />
          </button>
          <button
            onClick={() => setDeleteConfirm(row)}
            className="rounded-md p-1.5 text-muted-foreground hover:bg-destructive/10 hover:text-destructive transition-colors"
            aria-label={`Excluir ${row.name}`}
          >
            <Trash2 className="h-4 w-4" />
          </button>
        </div>
      ),
    },
  ];

  return (
    <div className="p-6">
      {/* Header */}
      <div className="mb-6 flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-foreground flex items-center gap-2">
            <Package className="h-6 w-6 text-primary" />
            Produtos
          </h1>
          <p className="mt-1 text-sm text-muted-foreground">
            Gerencie o cadastro de produtos do estabelecimento
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" onClick={() => setImportModalOpen(true)}>
            <Upload className="h-4 w-4" />
            Importar CSV
          </Button>
          <Button onClick={openCreateModal}>
            <Plus className="h-4 w-4" />
            Novo Produto
          </Button>
        </div>
      </div>

      {/* Search and Filters */}
      <div className="mb-4 flex flex-wrap items-center gap-3">
        <div className="relative flex-1 min-w-[200px] max-w-sm">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <input
            type="text"
            placeholder="Buscar por nome ou código..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="flex h-10 w-full rounded-lg border border-input bg-background pl-9 pr-3 text-sm placeholder:text-muted-foreground/50 focus:outline-none focus:ring-2 focus:ring-primary/50 focus:border-primary transition-all"
          />
        </div>
        <select
          value={categoryFilter}
          onChange={(e) => setCategoryFilter(e.target.value)}
          className="h-10 rounded-lg border border-input bg-background px-3 text-sm focus:outline-none focus:ring-2 focus:ring-primary/50 focus:border-primary transition-all"
        >
          <option value="">Todas categorias</option>
          {categories.map((cat) => (
            <option key={cat} value={cat}>{cat}</option>
          ))}
        </select>
        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          className="h-10 rounded-lg border border-input bg-background px-3 text-sm focus:outline-none focus:ring-2 focus:ring-primary/50 focus:border-primary transition-all"
        >
          <option value="">Todos status</option>
          <option value="normal">Normal</option>
          <option value="baixo">Baixo</option>
          <option value="esgotado">Esgotado</option>
        </select>
      </div>

      {/* Stock Summary */}
      {!loading && products.length > 0 && (
        <div className="mb-4 flex items-center gap-6 rounded-lg border border-border/50 bg-card/50 px-4 py-3">
          <div>
            <span className="text-xs text-muted-foreground">Total de Produtos</span>
            <p className="text-lg font-bold text-foreground">{pagination.total}</p>
          </div>
          <div className="h-8 w-px bg-border" />
          <div>
            <span className="text-xs text-muted-foreground">Valor em Estoque (Custo)</span>
            <p className="text-lg font-bold text-foreground">
              {formatBRL(products.reduce((sum, p) => sum + Number(p.stock_quantity) * Number(p.cost_price), 0))}
            </p>
          </div>
          <div className="h-8 w-px bg-border" />
          <div>
            <span className="text-xs text-muted-foreground">Valor em Estoque (Venda)</span>
            <p className="text-lg font-bold text-primary">
              {formatBRL(products.reduce((sum, p) => sum + Number(p.stock_quantity) * Number(p.sale_price), 0))}
            </p>
          </div>
        </div>
      )}

      {/* Stock Low Alert Banner */}
      {!loading && products.some(p => p.status === 'baixo' || p.status === 'esgotado') && (
        <div className="mb-4 rounded-lg border border-amber-500/30 bg-amber-500/5 px-4 py-3">
          <div className="flex items-center gap-2">
            <AlertTriangle className="h-5 w-5 text-amber-500" />
            <span className="text-sm font-medium text-amber-400">
              Atenção: {products.filter(p => p.status === 'baixo').length} produto(s) com estoque baixo
              {products.some(p => p.status === 'esgotado') && `, ${products.filter(p => p.status === 'esgotado').length} esgotado(s)`}
            </span>
          </div>
          <div className="mt-2 flex flex-wrap gap-2">
            {products.filter(p => p.status === 'baixo' || p.status === 'esgotado').slice(0, 5).map(p => (
              <span key={p.id} className={`rounded-full px-2 py-0.5 text-xs font-medium ${p.status === 'esgotado' ? 'bg-red-500/10 text-red-400' : 'bg-amber-500/10 text-amber-400'}`}>
                {p.name}: {p.stock_quantity} {p.unit}
              </span>
            ))}
          </div>
        </div>
      )}

      {/* Table */}
      {loading ? (
        <div className="flex h-64 items-center justify-center">
          <div className="h-8 w-8 animate-spin rounded-full border-2 border-primary border-t-transparent" />
        </div>
      ) : (
        <>
          <DataTable
            columns={columns}
            data={products}
            getRowKey={(row) => row.id}
            emptyMessage="Nenhum produto encontrado"
          />
          {/* Pagination */}
          {pagination.totalPages > 1 && (
            <div className="mt-4 flex items-center justify-between">
              <span className="text-sm text-muted-foreground">
                {pagination.total} produto(s) encontrado(s)
              </span>
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  disabled={pagination.page <= 1}
                  onClick={() => fetchProducts(pagination.page - 1)}
                >
                  Anterior
                </Button>
                <span className="text-sm text-muted-foreground">
                  Página {pagination.page} de {pagination.totalPages}
                </span>
                <Button
                  variant="outline"
                  size="sm"
                  disabled={pagination.page >= pagination.totalPages}
                  onClick={() => fetchProducts(pagination.page + 1)}
                >
                  Próxima
                </Button>
              </div>
            </div>
          )}
        </>
      )}

      {/* Create/Edit Modal */}
      <Modal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        title={editingProduct ? 'Editar Produto' : stockEntryMode ? '📦 Entrada de Estoque' : 'Novo Produto'}
        description={editingProduct ? 'Atualize as informações do produto' : stockEntryMode ? `Produto: ${stockEntryMode.productName} | Estoque atual: ${stockEntryMode.currentStock}` : 'Preencha os dados para cadastrar um novo produto'}
        className="max-w-2xl"
      >
        <form onSubmit={handleSubmit} className="space-y-4">
          {stockEntryMode ? (
            <>
              <div className="rounded-xl border border-green-500/30 bg-green-500/5 p-5">
                <div className="flex items-center gap-3 mb-4">
                  <div className="flex h-10 w-10 items-center justify-center rounded-full bg-green-500/20">
                    <span className="text-xl">+</span>
                  </div>
                  <div>
                    <p className="font-semibold text-foreground text-lg">{stockEntryMode.productName}</p>
                    <p className="text-sm text-muted-foreground">Estoque atual: <span className="font-bold text-primary">{stockEntryMode.currentStock}</span> unidades</p>
                  </div>
                </div>
                <div className="mt-4">
                  <label htmlFor="entry-quantity" className="mb-2 block text-sm font-semibold text-foreground">
                    Quantidade que chegou
                  </label>
                  <input
                    id="entry-quantity"
                    type="number"
                    step="1"
                    min="1"
                    value={formData.stock_quantity}
                    onChange={(e) => setFormData((p) => ({ ...p, stock_quantity: e.target.value }))}
                    placeholder="Ex: 24"
                    autoFocus
                    className="flex h-14 w-full rounded-xl border border-green-500/50 bg-background px-4 text-2xl font-bold text-center text-foreground placeholder:text-muted-foreground/50 focus:outline-none focus:ring-2 focus:ring-green-500/50 focus:border-green-500 transition-all"
                  />
                  {formData.stock_quantity && Number(formData.stock_quantity) > 0 && (
                    <p className="mt-3 text-center text-sm text-muted-foreground">
                      Novo estoque total: <span className="font-bold text-green-400 text-lg">{stockEntryMode.currentStock + Number(formData.stock_quantity)}</span> unidades
                    </p>
                  )}
                </div>
              </div>
              <div className="flex justify-between items-center pt-3">
                <button type="button" className="text-xs text-muted-foreground hover:text-foreground underline" onClick={() => setStockEntryMode(null)}>
                  Cadastrar como novo produto
                </button>
                <div className="flex gap-2">
                  <Button type="button" variant="outline" onClick={() => setModalOpen(false)}>
                    Cancelar
                  </Button>
                  <button type="submit" disabled={submitting || !formData.stock_quantity || Number(formData.stock_quantity) <= 0} className="rounded-lg bg-green-600 px-6 py-2.5 text-sm font-bold text-white hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors">
                    {submitting ? 'Salvando...' : 'Dar Entrada'}
                  </button>
                </div>
              </div>
            </>
          ) : (
          <>
          {stockEntryMode && (
            <div className="rounded-lg border border-green-500/30 bg-green-500/10 px-4 py-3 mb-2">
              <p className="text-sm font-medium text-green-400">✓ Produto encontrado no sistema</p>
              <p className="text-xs text-green-300/70 mt-1">Informe apenas a quantidade que chegou. Os demais campos já estão preenchidos.</p>
            </div>
          )}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label htmlFor="product-barcode" className="mb-1.5 block text-xs font-medium text-muted-foreground">
                Código de Barras
              </label>
              <input
                id="product-barcode"
                type="text"
                value={formData.barcode}
                onChange={(e) => { setFormData((p) => ({ ...p, barcode: e.target.value })); setStockEntryMode(null); }}
                onBlur={(e) => checkBarcode(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); checkBarcode(formData.barcode); } }}
                className={`flex h-10 w-full rounded-lg border bg-background px-3 text-sm transition-all focus:outline-none focus:ring-2 focus:ring-primary/50 focus:border-primary ${formErrors.barcode ? 'border-destructive' : 'border-input'}`}
              />
              {formErrors.barcode && <p className="mt-1 text-xs text-destructive">{formErrors.barcode}</p>}
            </div>
            <div>
              <label htmlFor="product-name" className="mb-1.5 block text-xs font-medium text-muted-foreground">
                Nome
              </label>
              <input
                id="product-name"
                type="text"
                value={formData.name}
                onChange={(e) => setFormData((p) => ({ ...p, name: e.target.value }))}
                className={`flex h-10 w-full rounded-lg border bg-background px-3 text-sm transition-all focus:outline-none focus:ring-2 focus:ring-primary/50 focus:border-primary ${formErrors.name ? 'border-destructive' : 'border-input'}`}
              />
              {formErrors.name && <p className="mt-1 text-xs text-destructive">{formErrors.name}</p>}
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label htmlFor="product-sale-price" className="mb-1.5 block text-xs font-medium text-muted-foreground">
                Preço de Venda (R$)
              </label>
              <input
                id="product-sale-price"
                type="number"
                step="0.01"
                min="0.01"
                value={formData.sale_price}
                onChange={(e) => setFormData((p) => ({ ...p, sale_price: e.target.value }))}
                className={`flex h-10 w-full rounded-lg border bg-background px-3 text-sm transition-all focus:outline-none focus:ring-2 focus:ring-primary/50 focus:border-primary ${formErrors.sale_price ? 'border-destructive' : 'border-input'}`}
              />
              {formErrors.sale_price && <p className="mt-1 text-xs text-destructive">{formErrors.sale_price}</p>}
            </div>
            <div>
              <label htmlFor="product-cost-price" className="mb-1.5 block text-xs font-medium text-muted-foreground">
                Preço de Custo (R$)
              </label>
              <input
                id="product-cost-price"
                type="number"
                step="0.01"
                min="0"
                value={formData.cost_price}
                onChange={(e) => setFormData((p) => ({ ...p, cost_price: e.target.value }))}
                className={`flex h-10 w-full rounded-lg border bg-background px-3 text-sm transition-all focus:outline-none focus:ring-2 focus:ring-primary/50 focus:border-primary ${formErrors.cost_price ? 'border-destructive' : 'border-input'}`}
              />
              {formErrors.cost_price && <p className="mt-1 text-xs text-destructive">{formErrors.cost_price}</p>}
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label htmlFor="product-unit" className="mb-1.5 block text-xs font-medium text-muted-foreground">
                Unidade
              </label>
              <select
                id="product-unit"
                value={formData.unit}
                onChange={(e) => setFormData((p) => ({ ...p, unit: e.target.value }))}
                className="flex h-10 w-full rounded-lg border border-input bg-background px-3 text-sm transition-all focus:outline-none focus:ring-2 focus:ring-primary/50 focus:border-primary"
              >
                <option value="un">Unidade (un)</option>
                <option value="kg">Quilograma (kg)</option>
                <option value="litro">Litro</option>
                <option value="metro">Metro</option>
              </select>
            </div>
            <div>
              <label htmlFor="product-category" className="mb-1.5 block text-xs font-medium text-muted-foreground">
                Categoria
              </label>
              {(formData.category === '__new__' || (!categories.includes(formData.category) && formData.category !== '')) ? (
                <div className="flex gap-2">
                  <input
                    id="product-category"
                    type="text"
                    value={formData.category === '__new__' ? '' : formData.category}
                    onChange={(e) => setFormData((p) => ({ ...p, category: e.target.value }))}
                    className={`flex h-10 w-full rounded-lg border bg-background px-3 text-sm transition-all focus:outline-none focus:ring-2 focus:ring-primary/50 focus:border-primary ${formErrors.category ? 'border-destructive' : 'border-input'}`}
                    placeholder="Nome da nova categoria"
                  />
                  <button
                    type="button"
                    onClick={() => setFormData((p) => ({ ...p, category: '' }))}
                    className="h-10 rounded-lg border border-input px-3 text-sm text-muted-foreground hover:bg-muted"
                  >
                    Cancelar
                  </button>
                </div>
              ) : (
                <select
                  id="product-category"
                  value={formData.category}
                  onChange={(e) => setFormData((p) => ({ ...p, category: e.target.value }))}
                  className={`flex h-10 w-full rounded-lg border bg-background px-3 text-sm transition-all focus:outline-none focus:ring-2 focus:ring-primary/50 focus:border-primary ${formErrors.category ? 'border-destructive' : 'border-input'}`}
                >
                  <option value="">Selecione uma categoria</option>
                  {categories.map((cat) => (
                    <option key={cat} value={cat}>{cat}</option>
                  ))}
                  <option value="__new__">+ Nova categoria...</option>
                </select>
              )}
              {formErrors.category && <p className="mt-1 text-xs text-destructive">{formErrors.category}</p>}
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label htmlFor="product-stock" className="mb-1.5 block text-xs font-medium text-muted-foreground">
                Estoque Atual
              </label>
              <input
                id="product-stock"
                type="number"
                step="0.001"
                min="0"
                value={formData.stock_quantity}
                onChange={(e) => setFormData((p) => ({ ...p, stock_quantity: e.target.value }))}
                className="flex h-10 w-full rounded-lg border border-input bg-background px-3 text-sm transition-all focus:outline-none focus:ring-2 focus:ring-primary/50 focus:border-primary"
              />
            </div>
            <div>
              <label htmlFor="product-min-stock" className="mb-1.5 block text-xs font-medium text-muted-foreground">
                Nível Mínimo
              </label>
              <input
                id="product-min-stock"
                type="number"
                step="0.001"
                min="0"
                value={formData.min_stock_level}
                onChange={(e) => setFormData((p) => ({ ...p, min_stock_level: e.target.value }))}
                className="flex h-10 w-full rounded-lg border border-input bg-background px-3 text-sm transition-all focus:outline-none focus:ring-2 focus:ring-primary/50 focus:border-primary"
              />
            </div>
          </div>

          <div>
            <label htmlFor="product-supplier" className="mb-1.5 block text-xs font-medium text-muted-foreground">
              Fornecedor
            </label>
            <select
              id="product-supplier"
              value={formData.supplier_id}
              onChange={(e) => setFormData((p) => ({ ...p, supplier_id: e.target.value }))}
              className="flex h-10 w-full rounded-lg border border-input bg-background px-3 text-sm transition-all focus:outline-none focus:ring-2 focus:ring-primary/50 focus:border-primary"
            >
              <option value="">Nenhum</option>
              {suppliers.map((s) => (
                <option key={s.id} value={s.id}>{s.company_name}</option>
              ))}
            </select>
          </div>

          <div className="flex justify-end gap-2 pt-2">
            <Button type="button" variant="outline" onClick={() => setModalOpen(false)}>
              Cancelar
            </Button>
            <Button type="submit" disabled={submitting}>
              {submitting ? 'Salvando...' : editingProduct ? 'Atualizar' : stockEntryMode ? 'Dar Entrada' : 'Criar'}
            </Button>
          </div>
          </>
          )}
        </form>
      </Modal>

      {/* Import CSV Modal */}
      <ProductImportModal
        isOpen={importModalOpen}
        onClose={() => setImportModalOpen(false)}
        onSuccess={() => fetchProducts(pagination.page)}
      />

      {/* Delete Confirmation Modal */}
      {deleteConfirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm">
          <div className="w-full max-w-sm rounded-2xl border border-border bg-card p-6 shadow-2xl">
            <div className="mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-destructive/10">
              <Trash2 className="h-6 w-6 text-destructive" />
            </div>
            <h3 className="text-lg font-semibold text-foreground">Excluir produto?</h3>
            <p className="mt-2 text-sm text-muted-foreground">
              O produto &ldquo;{deleteConfirm.name}&rdquo; será removido. Esta ação não pode ser desfeita.
            </p>
            <div className="mt-6 flex gap-3">
              <button
                onClick={() => setDeleteConfirm(null)}
                className="flex-1 rounded-xl border border-border px-4 py-2.5 text-sm font-medium text-foreground transition-colors hover:bg-muted"
              >
                Cancelar
              </button>
              <button
                onClick={handleDelete}
                className="flex-1 rounded-xl bg-destructive px-4 py-2.5 text-sm font-medium text-white transition-colors hover:bg-destructive/90"
              >
                Excluir
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export function ProductsPage() {
  return (
    <RequireRole roles={['gerente']}>
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3 }}
      >
        <ProductsPageContent />
      </motion.div>
    </RequireRole>
  );
}
